import { jsonStringifyDefi } from "./json-stringify-defi"

/**
 * Metrics for a single RPC call within a chunk
 */
export interface RpcCallMetrics {
  /**
   * Size of the chunk (number of items requested)
   */
  chunkSize: number

  /**
   * Index of this chunk (0-based)
   */
  chunkIndex: number

  /**
   * Response size in bytes (estimated from JSON stringification)
   */
  responseSizeBytes: number

  /**
   * Whether this RPC call succeeded or failed
   */
  success: boolean

  /**
   * Error message if the call failed
   */
  error?: string

  /**
   * Timestamp when the RPC call was made
   */
  timestamp: number

  /**
   * Duration of the RPC call in milliseconds
   */
  durationMs: number
}

/**
 * Aggregated metrics for a single query execution
 */
export interface RpcExecutionMetrics {
  /**
   * Total number of chunks processed
   */
  totalChunks: number

  /**
   * Total number of RPC calls made
   */
  totalRpcCalls: number

  /**
   * Number of successful RPC calls
   */
  successfulRpcCalls: number

  /**
   * Number of failed RPC calls
   */
  failedRpcCalls: number

  /**
   * Total response size in bytes across all chunks
   */
  totalResponseSizeBytes: number

  /**
   * Average response size per chunk in bytes
   */
  averageResponseSizeBytes: number

  /**
   * Average response size per item (bytes per item in chunk)
   * This is the key metric for RPC profiling
   */
  averageBytesPerItem: number

  /**
   * Chunk size used (assumes all chunks use same size)
   */
  chunkSize: number

  /**
   * Individual RPC call metrics
   */
  rpcCalls: RpcCallMetrics[]

  /**
   * Timestamp when execution started
   */
  executionStartTime: number

  /**
   * Timestamp when execution completed
   */
  executionEndTime: number
}

/**
 * Collector for RPC metrics during query execution.
 * Query functions use this to report RPC call metrics.
 */
export class RpcMetricsCollector {
  private metrics: RpcCallMetrics[] = []
  private chunkSize: number | undefined
  private executionStartTime: number

  constructor() {
    this.executionStartTime = Date.now()
  }

  /**
   * Record an RPC call
   */
  recordRpcCall(
    chunkSize: number,
    chunkIndex: number,
    responseData: unknown,
    success: boolean,
    error?: string,
    durationMs?: number,
  ): void {
    // Estimate response size by stringifying (approximate)
    const responseSizeBytes = success ? new Blob([jsonStringifyDefi(responseData, null, 0)]).size : 0

    this.chunkSize = chunkSize // Store chunk size (assumes all chunks are same size)

    this.metrics.push({
      chunkSize,
      chunkIndex,
      responseSizeBytes,
      success,
      error,
      timestamp: Date.now(),
      durationMs: durationMs ?? 0,
    })
  }

  /**
   * Get aggregated metrics
   */
  getMetrics(): RpcExecutionMetrics {
    const executionEndTime = Date.now()
    const totalRpcCalls = this.metrics.length
    const successfulRpcCalls = this.metrics.filter((m) => m.success).length
    const failedRpcCalls = this.metrics.filter((m) => !m.success).length
    const totalResponseSizeBytes = this.metrics.reduce((sum, m) => sum + m.responseSizeBytes, 0)
    const averageResponseSizeBytes = this.metrics.length > 0 ? totalResponseSizeBytes / this.metrics.length : 0

    // Calculate average bytes per item
    // Sum of (responseSizeBytes / chunkSize) for all successful calls
    const totalItems = this.metrics.filter((m) => m.success).reduce((sum, m) => sum + m.chunkSize, 0)
    const averageBytesPerItem = totalItems > 0 ? totalResponseSizeBytes / totalItems : 0

    // Get unique chunk sizes (should typically be 1, but handle multiple)
    const uniqueChunkSizes = [...new Set(this.metrics.map((m) => m.chunkSize))]
    const chunkSize = uniqueChunkSizes.length === 1 ? uniqueChunkSizes[0]! : 0

    return {
      totalChunks: this.metrics.length,
      totalRpcCalls,
      successfulRpcCalls,
      failedRpcCalls,
      totalResponseSizeBytes,
      averageResponseSizeBytes,
      averageBytesPerItem,
      chunkSize,
      rpcCalls: [...this.metrics],
      executionStartTime: this.executionStartTime,
      executionEndTime,
    }
  }

  /**
   * Reset the collector for a new execution
   */
  reset(): void {
    this.metrics = []
    this.chunkSize = undefined
    this.executionStartTime = Date.now()
  }
}
